import type { AuthUser } from '../types/chat';
import { decodeJwtPayload } from '../utils/jwt';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const TOKEN_STORAGE_KEY = 'jwt_token';

export class AuthService {
  private token: string | null = null;

  constructor() {
    this.token = this.getStoredToken();
  }

  /**
   * Get the stored JWT token
   */
  getToken(): string | null {
    if (!this.token) {
      this.token = this.getStoredToken();
    }
    return this.token;
  }

  /**
   * Set and store the JWT token
   */
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  }

  /**
   * Remove the JWT token
   */
  removeToken(): void {
    this.token = null;
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  /**
   * Get token from localStorage
   */
  private getStoredToken(): string | null {
    try {
      return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Check if user is authenticated by checking token validity
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    try {
      // Decode JWT payload to check expiration using shared utility
      const payload = decodeJwtPayload(token);
      const currentTime = Math.floor(Date.now() / 1000);
      const isValid = payload.exp > currentTime;

      return isValid;
    } catch {
      return false;
    }
  }

  /**
   * Check if user has admin role
   */
  isAdmin(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    try {
      // Decode JWT payload to check roles using shared utility
      const payload = decodeJwtPayload(token);
      const authorities = payload.authorities || [];
      return Array.isArray(authorities) && authorities.includes('ROLE_ADMIN');
    } catch {
      return false;
    }
  }

  /**
   * Get current authenticated user info from backend
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    const token = this.getToken();

    if (!token || !this.isAuthenticated()) {
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.removeToken();
        }
        return null;
      }

      const userData = await response.json();
      return userData;
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  /**
   * Redirect to OAuth2 login with Google
   */
  loginWithGoogle(): void {
    window.location.href = `${API_BASE_URL}/oauth2/authorization/google`;
  }

  /**
   * Redirect to OAuth2 login with Facebook
   */
  loginWithFacebook(): void {
    window.location.href = `${API_BASE_URL}/oauth2/authorization/facebook`;
  }

  /**
   * Logout by removing token
   */
  logout(): void {
    this.removeToken();
    // Optionally redirect to home or login page
    window.location.href = '/';
  }

  /**
   * Handle OAuth2 callback by extracting token from URL
   */
  handleCallback(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      this.setToken(token);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return token;
    }

    return null;
  }

  /**
   * Check authentication status with backend
   */
  async checkAuthStatus(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/status`);
      if (response.ok) {
        const status = await response.json();
        return status.authenticated;
      }
      return false;
    } catch {
      return false;
    }
  }
}

export const authService = new AuthService();
